<script setup>
import { computed, ref } from 'vue';
import StatTile from './stat-tile.vue';

/**
 * Which packages customers buy together in one cart, and how those combos perform.
 *
 * Only paid customer orders count: staff-entered orders and the fee lines staff add later are left
 * out on the server, so a combo here is something the customer chose. Every figure and change is
 * the server's; this component only ranks, scales and formats them.
 */
const props = defineProps({
  cart: {
    type: Object,
    required: true,
  },
  /** The comparison window as text, or an empty string when nothing is compared. */
  comparisonLabel: {
    type: String,
    default: '',
  },
});

const LEVEL = { PACKAGE: 'package', CATEGORY: 'category' };
const RANK = { ORDERS: 'orders', REVENUE: 'revenue' };
const TOP = 10;

const level = ref(LEVEL.PACKAGE);
const rank = ref(RANK.ORDERS);
const showAll = ref(false);

const summary = computed(() => props.cart.summary);
const premium = computed(() => props.cart.premium);
const isComparing = computed(() => Boolean(props.cart.comparison));

const rows = computed(() => {
  const source = level.value === LEVEL.PACKAGE ? props.cart.combos : props.cart.categoryCombos;
  const key = rank.value === RANK.ORDERS ? 'orders' : 'revenue';

  return [...source].sort((a, b) => b[key] - a[key] || b.orders - a.orders);
});

const visibleRows = computed(() => (showAll.value ? rows.value : rows.value.slice(0, TOP)));
const hiddenCount = computed(() => Math.max(0, rows.value.length - TOP));
const peak = computed(() => Math.max(1, ...rows.value.map(row => (rank.value === RANK.ORDERS ? row.orders : row.revenue))));

/**
 * Bar width against the top row, so the leader fills the track and the rest read relative to it.
 * @param {object} row Combo.
 * @returns {string}
 */
function barWidth(row) {
  const value = rank.value === RANK.ORDERS ? row.orders : row.revenue;
  return `${Math.max(1, (value / peak.value) * 100)}%`;
}

const number = new Intl.NumberFormat('en-US');
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyExact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Percent change of a pair, or null when there is nothing to show.
 * @param {object|null} pair Change.
 * @returns {number|null}
 */
function percentOf(pair) {
  return pair?.percent ?? null;
}

/**
 * A change as an arrow and a percentage. A combo with no sales in the earlier window is "New".
 * @param {object|null} pair Change.
 * @param {number|null} previous The earlier window's figure.
 * @returns {{text: string, tone: string}|null}
 */
function changeBadge(pair, previous) {
  if (!pair) {
    return null;
  }
  if (pair.percent === null) {
    return previous === 0 && pair.absolute > 0 ? { text: 'New', tone: 'up' } : null;
  }
  if (pair.percent === 0) {
    return { text: '→ 0.0%', tone: 'flat' };
  }
  const up = pair.percent > 0;
  return { text: `${up ? '↗ +' : '↘ '}${pair.percent.toFixed(1)}%`, tone: up ? 'up' : 'down' };
}

/**
 * A move in percentage points, for a rate.
 * @param {object|undefined} pair Change.
 * @returns {string}
 */
function pointsLabel(pair) {
  if (!pair) {
    return '';
  }
  const sign = pair.absolute > 0 ? '+' : '';
  return `${sign}${pair.absolute.toFixed(1)} pts`;
}

/**
 * Direction of a change for a tile, which only needs a signed number.
 * @param {object|undefined} pair Change.
 * @returns {number|null}
 */
function directionOf(pair) {
  return pair ? pair.absolute : null;
}

/**
 * A rate's move in points as a badge.
 * @param {object|null} pair Change.
 * @returns {{text: string, tone: string}|null}
 */
function pointsBadge(pair) {
  if (!pair) {
    return null;
  }
  let tone = 'flat';
  if (pair.absolute > 0) {
    tone = 'up';
  } else if (pair.absolute < 0) {
    tone = 'down';
  }
  return { text: pointsLabel(pair), tone };
}

const premiumFacts = computed(() => {
  const { change, previous } = premium.value;

  return [
    {
      label: 'Premium orders',
      value: number.format(premium.value.premiumOrders),
      badge: changeBadge(change.premiumOrders, previous?.premiumOrders),
    },
    {
      label: 'Share of Food Manager orders',
      value: `${premium.value.premiumShare.toFixed(1)}%`,
      badge: pointsBadge(change.premiumShare),
      hint: `${number.format(premium.value.premiumOrders)} of ${number.format(premium.value.foodManagerOrders)}`,
    },
    {
      label: 'Free cards issued',
      value: number.format(premium.value.freeCards),
      badge: changeBadge(change.freeCards, previous?.freeCards),
    },
  ];
});

const levelNote = computed(() => (level.value === LEVEL.PACKAGE
  ? 'The exact packages in one paid cart.'
  : 'The product lines in one paid cart, whichever packages stand for them.'));
</script>

<template>
  <div :class="$style.stack">
    <p v-if="summary.customerOrders === 0" :class="$style.empty">
      No paid customer orders in this window.
    </p>

    <template v-else>
      <div :class="$style.tiles">
        <stat-tile
          label="Customer orders"
          hint="Paid, placed by the customer"
          :value="number.format(summary.customerOrders)"
          :change-percent="percentOf(summary.change.customerOrders)"
          tone="teal"
        />
        <stat-tile
          label="Combo rate"
          :hint="`${number.format(summary.comboOrders)} orders with 2+ products`"
          :value="`${summary.comboRate.toFixed(1)}%`"
          :change-percent="directionOf(summary.change.comboRate)"
          :change-label="pointsLabel(summary.change.comboRate)"
          tone="orange"
        />
        <stat-tile
          label="Avg order, combos"
          :hint="`${number.format(summary.comboOrders)} orders`"
          :value="moneyExact.format(summary.comboAverageOrderValue)"
          :change-percent="percentOf(summary.change.comboAverageOrderValue)"
          tone="teal"
        />
        <stat-tile
          label="Avg order, single product"
          :hint="`${number.format(summary.singleOrders)} orders`"
          :value="moneyExact.format(summary.singleAverageOrderValue)"
          :change-percent="percentOf(summary.change.singleAverageOrderValue)"
          tone="red"
        />
      </div>

      <p v-if="isComparing && comparisonLabel" :class="$style.note">
        Changes are against {{ comparisonLabel }}.
      </p>

      <div :class="$style.card">
        <h3 :class="$style.title">
          Premium Food Manager
        </h3>
        <p :class="$style.note">
          Premium includes a free Food Handler card, added to the order at no charge.
        </p>
        <div :class="$style.facts">
          <div
            v-for="fact in premiumFacts"
            :key="fact.label"
            :class="$style.fact"
          >
            <span :class="$style.factLabel">{{ fact.label }}</span>
            <strong :class="$style.factValue">{{ fact.value }}</strong>
            <span v-if="fact.badge" :class="[$style.change, $style[fact.badge.tone]]">{{ fact.badge.text }}</span>
            <span v-if="fact.hint" :class="$style.factHint">{{ fact.hint }}</span>
          </div>
        </div>
      </div>

      <div :class="$style.card">
        <div :class="$style.header">
          <h3 :class="$style.title">
            Combos
          </h3>
          <div :class="$style.controls">
            <div
              :class="$style.toggle"
              role="group"
              aria-label="Combo level"
            >
              <button
                type="button"
                :class="[$style.toggleButton, level === LEVEL.PACKAGE ? $style.toggleActive : '']"
                @click="level = LEVEL.PACKAGE; showAll = false"
              >
                Package
              </button>
              <button
                type="button"
                :class="[$style.toggleButton, level === LEVEL.CATEGORY ? $style.toggleActive : '']"
                @click="level = LEVEL.CATEGORY; showAll = false"
              >
                Category
              </button>
            </div>
            <div
              :class="$style.toggle"
              role="group"
              aria-label="Rank by"
            >
              <button
                type="button"
                :class="[$style.toggleButton, rank === RANK.ORDERS ? $style.toggleActive : '']"
                @click="rank = RANK.ORDERS"
              >
                By orders
              </button>
              <button
                type="button"
                :class="[$style.toggleButton, rank === RANK.REVENUE ? $style.toggleActive : '']"
                @click="rank = RANK.REVENUE"
              >
                By revenue
              </button>
            </div>
          </div>
        </div>

        <p :class="$style.note">
          {{ levelNote }} Share is of the {{ number.format(summary.comboOrders) }} orders with 2+ products.
        </p>

        <p v-if="rows.length === 0" :class="$style.empty">
          No combos in this window.
        </p>

        <ul v-else :class="$style.rows">
          <li
            v-for="combo in visibleRows"
            :key="combo.key"
            :class="$style.row"
          >
            <span :class="$style.rowHead">
              <span :class="$style.rowLabel">
                {{ combo.label }}
                <span
                  v-if="combo.includesFreeCard"
                  :class="$style.promo"
                >Premium offer</span>
              </span>
              <span :class="$style.rowValue">
                {{ rank === RANK.ORDERS ? number.format(combo.orders) : money.format(combo.revenue) }}
                <span :class="$style.rowUnit">{{ rank === RANK.ORDERS ? 'orders' : '' }}</span>
              </span>
            </span>
            <span :class="$style.track">
              <span :class="$style.bar" :style="{ width: barWidth(combo) }" />
            </span>
            <span :class="$style.rowFoot">
              <span>{{ number.format(combo.orders) }} orders</span>
              <span
                v-if="changeBadge(combo.ordersChange, combo.previousOrders)"
                :class="$style[changeBadge(combo.ordersChange, combo.previousOrders).tone]"
              >
                {{ changeBadge(combo.ordersChange, combo.previousOrders).text }}
              </span>
              <span>{{ money.format(combo.revenue) }}</span>
              <span
                v-if="changeBadge(combo.revenueChange, combo.previousRevenue)"
                :class="$style[changeBadge(combo.revenueChange, combo.previousRevenue).tone]"
              >
                {{ changeBadge(combo.revenueChange, combo.previousRevenue).text }}
              </span>
              <span>{{ moneyExact.format(combo.averageOrderValue) }} avg</span>
              <span>{{ combo.share.toFixed(1) }}% of combos</span>
            </span>
          </li>
        </ul>

        <button
          v-if="hiddenCount > 0"
          type="button"
          :class="$style.more"
          @click="showAll = !showAll"
        >
          {{ showAll ? 'Show top 10' : `Show ${hiddenCount} more` }}
        </button>
      </div>
    </template>
  </div>
</template>

<style module>
.stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
}
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.header {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
}
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.toggle {
  display: flex;
  gap: 4px;
  background: var(--background-color);
  border-radius: var(--border-radius-xxl);
  padding: 4px;
}
.toggleButton {
  border: none;
  background: transparent;
  border-radius: var(--border-radius-xxl);
  padding: 6px 14px;
  font-size: var(--font-size-lg);
  color: var(--primary-font-color);
  cursor: pointer;
}
.toggleActive {
  background: white;
  color: var(--primary-font-dark-color);
  box-shadow: 0 1px 2px var(--shadow-color);
}
.note {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
}
.fact {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.factLabel {
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
}
.factValue {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--primary-font-dark-color);
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
}
.factHint {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.change {
  font-size: var(--font-size-lg);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.row {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.rowHead {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}
.rowLabel {
  font-size: var(--font-size-lg);
  color: var(--primary-font-dark-color);
  min-width: 0;
  overflow-wrap: anywhere;
}
.promo {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: var(--border-radius-xxl);
  background: var(--background-color);
  color: var(--orange-dark);
  font-size: var(--font-size-md);
  font-weight: 500;
  white-space: nowrap;
}
.rowValue {
  font-size: var(--font-size-lg);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.rowUnit {
  font-weight: 400;
  color: var(--faint-color);
}
.track {
  display: block;
  height: 6px;
  border-radius: 3px;
  background: var(--background-color);
  overflow: hidden;
}
.bar {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--pfs-orange);
  opacity: 0.75;
}
.rowFoot {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  font-size: var(--font-size-md);
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
}
.up { color: var(--green-light); }
.down { color: var(--button-red); }
.flat { color: var(--faint-color); }
.more {
  align-self: center;
  border: none;
  background: transparent;
  color: var(--pfs-teal);
  font-size: var(--font-size-lg);
  cursor: pointer;
  padding: 6px 12px;
}
.empty {
  margin: 0;
  padding: 18px 0;
  text-align: center;
  color: var(--faint-color);
  font-size: var(--font-size-lg);
}
</style>
